import { inject } from '@angular/core';
import { ActivityEntityName, ItemDeletedResponse, JobItemCommandId, StageItemDetail, StageItemSecurityCapabilities } from '@jms/model';
import { injectQueryClient } from '@ngneat/query';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import { lastValueFrom } from 'rxjs';
import type { JobItemBoardParams } from '../../models/job.item.model';
import { resolveItemRecord } from '../row/item.row.record';

/** The scope a contractor row carries: the line, its job and its stage, as the parent-shaped row projects them. */
export interface ContractorItemRowScope {
  readonly itemId: string;
  readonly jobId?: string;
  readonly stageId?: string;
  readonly name?: string;
  readonly capabilities?: StageItemSecurityCapabilities;
}

export function resolveContractorItemRowScope(row: unknown): ContractorItemRowScope | null {

  const item = resolveItemRecord(row) as (StageItemDetail & { capabilities?: StageItemSecurityCapabilities }) | null;
  if (!item?._id) return null;

  const parent = row as { _id?: string; workOrders?: { _id?: string } } | null;

  return { itemId: item._id, jobId: parent?._id, stageId: parent?.workOrders?._id, name: item.name, capabilities: item.capabilities };
}

export interface ContractorItemRequestRefusal {
  readonly reason?: string;
}

// The pending status command answers a gate refusal (missing before photo, reason rule) as an outcome, not an HTTP error.
export async function requestContractorItemLine(entityData: EntityDataServiceRaw, scope: ContractorItemRowScope): Promise<ContractorItemRequestRefusal | null> {

  try {
    const outcome = await lastValueFrom(entityData.executeCommand<{ status?: 'succeeded' | 'skipped' | 'failed'; message?: string }>(ActivityEntityName.JobItem, JobItemCommandId.RequestItem, { itemId: scope.itemId, jobId: scope.jobId, stageId: scope.stageId }));
    return outcome?.status === 'succeeded' ? null : { reason: outcome?.message };
  } catch (httpError) {
    return { reason: (httpError as { error?: { message?: string } })?.error?.message };
  }
}

// Unstamped rows are the office's, whose lock lives in its status gates, so only a stamped refusal counts.
export function isContractorItemRowLocked(row: unknown): boolean {

  const capabilities = resolveContractorItemRowScope(row)?.capabilities;

  return !!capabilities && capabilities.canUpdate !== true;
}

/**
 * Row ⋯ menu for the contractor portal's boards (WBS-11932, WBS-11933).
 *
 * The office menu decides from the caller's Jobs rights, which a subcontractor never holds. Here the
 * server has already answered per row: the contractor strategy stamps `capabilities` on every line
 * from the stage's claim state and the line's status, so the menu only has to read them. Request and
 * Delete are the two things a subcontractor does to a line; Approve, Attach and Defects stay the office's.
 */
/** The SOR Details page offers Request as its header button and closes itself after a delete. */
export interface ContractorItemRowCommandOptions {
  readonly request?: boolean;
  readonly onDeleted?: () => void;
}

export function buildContractorItemRowCommands(row: unknown, params?: Pick<JobItemBoardParams, 'itemsChanged' | 'sorOnly'> | null, options?: ContractorItemRowCommandOptions): Command[] {

  const scope = resolveContractorItemRowScope(row);
  if (!scope) return [];

  const entityData = inject(EntityDataServiceRaw);
  const confirmService = inject(ConfirmServicePro);
  const toastService = inject(ToastService);
  const queryClient = injectQueryClient();

  const refreshItems = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [ActivityEntityName.JobItem, 'items'], refetchType: 'all' });
    params?.itemsChanged?.();
  };

  const name = scope.name ?? 'this item';
  const noun = params?.sorOnly?.() === true ? 'SOR' : 'Item';
  const commandScope = { itemId: scope.itemId, jobId: scope.jobId, stageId: scope.stageId };

  const onRequest = async (): Promise<void> => {
    const refusal = await requestContractorItemLine(entityData, scope);
    if (refusal) {
      toastService.error(`Cannot request ${noun}`, refusal.reason);
      return;
    }

    await refreshItems();
    toastService.success(`${noun} requested`, name);
  };

  const onDelete = async (): Promise<void> => {
    const confirmed = await confirmService.confirm('Delete item', `Delete ${name}? Any tasks on the item go with it.`, 'danger');
    if (!confirmed) return;

    let result: ItemDeletedResponse;
    try {
      result = await lastValueFrom(entityData.executeCommand<ItemDeletedResponse>(ActivityEntityName.JobItem, JobItemCommandId.DeleteItem, commandScope));
    } catch (httpError) {
      toastService.error('Cannot delete item', (httpError as { error?: { message?: string } })?.error?.message ?? undefined);
      return;
    }

    if (result && result.success === false) {
      toastService.error('Cannot delete item', result.error ?? undefined);
      return;
    }

    await refreshItems();
    toastService.success('Item deleted', name);
    options?.onDeleted?.();
  };

  // A line the server says is not theirs to change offers nothing: an office line, another company's
  // Draft, a Requested line. Showing a disabled command would name an action they can never take.
  return [
    ...(scope.capabilities?.canUpdate && options?.request !== false ? [new Command({ label: `Request ${noun}`, action: onRequest })] : []),
    ...(scope.capabilities?.canDelete ? [new Command({ label: 'Delete', action: onDelete })] : []),
  ];
}
